/**
 * Format a number as currency
 * @param {number} number - Number to format
 * @param {boolean} compact - Use compact notation (K, M)
 * @returns {string} - Formatted currency string
 */
export const formatPrice = (number, compact = true) => {
  if (number == null || isNaN(number)) return '$0';

  if (compact) {
    if (number >= 1000000) {
      return `$${(number / 1000000).toFixed(2)}M`;
    }
    if (number >= 1000) {
      return `$${(number / 1000).toFixed(1)}K`;
    }
  }

  return `$${number.toLocaleString()}`;
};

/**
 * Format a timestamp as time ago
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} - Formatted time ago string
 */
export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Unknown';

  const now = new Date();
  const then = new Date(timestamp);
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
};

/**
 * Format a number with thousand separators
 * @param {number} number - Number to format
 * @param {boolean} compact - Use compact notation
 * @returns {string} - Formatted number string
 */
export const formatNumber = (number, compact = true) => {
  if (number == null || isNaN(number)) return '0';

  if (compact) {
    if (number >= 1000000) {
      return `${(number / 1000000).toFixed(2)}M`;
    }
    if (number >= 1000) {
      return `${(number / 1000).toFixed(1)}K`;
    }
  }

  return number.toLocaleString();
};

/**
 * Format a decimal as percentage
 * @param {number} decimal - Decimal to format (e.g., 0.15 for 15%)
 * @param {boolean} showPlus - Show + sign for positive numbers
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (decimal, showPlus = true, decimals = 1) => {
  if (decimal == null || isNaN(decimal)) return '0%';

  const percent = (decimal * 100).toFixed(decimals);
  const sign = decimal > 0 && showPlus ? '+' : '';
  return `${sign}${percent}%`;
};

/**
 * Format domain length description
 * @param {string} domainName - Full domain name
 * @returns {string} - Length description
 */
export const formatDomainLength = (domainName) => {
  if (!domainName) return 'Unknown';

  const length = domainName.split('.')[0].length;
  if (length <= 5) return `Short (${length} chars)`;
  if (length <= 10) return `Medium (${length} chars)`;
  return `Long (${length} chars)`;
};

/**
 * Format a date
 * @param {string|Date} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'medium')
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, format = 'medium') => {
  if (!date) return 'Unknown';

  const d = new Date(date);

  if (format === 'short') {
    return d.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  } else if (format === 'long') {
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } else {
    // medium (default)
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * Format a duration in days to human readable
 * @param {number} days - Number of days
 * @returns {string} - Formatted duration
 */
export const formatDuration = (days) => {
  if (days == null || isNaN(days)) return 'Unknown';

  if (days < 1) return 'Less than a day';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? 's' : ''}`;
  }

  const years = Math.floor(days / 365);
  return `${years} year${years !== 1 ? 's' : ''}`;
};

/**
 * Format file size
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes == null || isNaN(bytes)) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format a rating (e.g., 4.8 to "4.8/5.0")
 * @param {number} rating - Rating number
 * @param {number} maxRating - Maximum rating (default 5)
 * @returns {string} - Formatted rating
 */
export const formatRating = (rating, maxRating = 5) => {
  if (rating == null || isNaN(rating)) return `0/${maxRating}`;
  return `${rating.toFixed(1)}/${maxRating.toFixed(1)}`;
};
